import { Component, inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import { settledCache } from '../../shared/utils/cache-ready';

import { Baa } from '../../objects/baa';
import { CreateEntity } from '../create-entity';
import { EntityType, DatabaseAction } from '../../objects/interface-library';

// Material imports
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatOption } from '@angular/material/core';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

// Services
import { BaaService } from '../../service/entity-services/baa.service';
import { CommonSheetsService } from '../../service/google-sheets/common-sheets.service';
import { FileUploadService } from '../../service/files-and-attachments/file-upload.service';
import { RelationshipService } from '../../service/entity-data-services/relationship.service';
import { IntakeAnalysisResponse } from '../../service/ai/document-analysis.service';

// Components
import { DocumentViewerSidenavComponent } from '../../entity-form-components/document-viewer-sidenav/document-viewer-sidenav.component';

// Utilities
import { generateBaaId } from '../../utils/id-generator';

import { PartyLabelPipe } from '../../shared/pipes/party-label.pipe';
@Component({
  selector: 'app-new-baa-full-page',
  templateUrl: './new-baa-full-page.component.html',
  styleUrls: ['./new-baa-full-page.component.css'],
  standalone: true,
  imports: [
    PartyLabelPipe,
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatCardModule,
    MatCheckboxModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatButtonModule,
    MatIconModule,
    MatSidenavModule,
    MatToolbarModule,
    MatChipsModule,
    MatDividerModule,
    MatProgressSpinner,
    MatOption,
    MatSlideToggleModule,
    DocumentViewerSidenavComponent
  ]
})
export class NewBaaFullPageComponent extends CreateEntity implements OnInit, OnDestroy {
  private commonSheetsService = inject(CommonSheetsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private fileUploadService = inject(FileUploadService);
  private relationshipService = inject(RelationshipService);
  private cdr = inject(ChangeDetectorRef);

  // Form management
  form: FormGroup;
  private formSubscription!: Subscription;
  
  currentBaa!: Baa;
  originalBaa: any = {};
  
  // UI State
  isEditMode = false;
  
  // File handling
  // File handling properties moved to CreateEntity base class

  // Static field data
  statusTypes: any[] = [];
  serviceTypes: any[] = [];
  renewalTypeLabels: any[] = [];
  allDepartments: any[] = [];
  sites: any[] = [];
  
  saving = false;
  loadingBaaData = false;

  // Workflow Request front-door: AI analysis state
  fromWorkflowRequest = false;
  intakeAnalysisResults: IntakeAnalysisResponse | null = null;
  showAISummary = false;
  aiSummary = '';
  autoPopulatedFieldsDisplay: { label: string; value: string; confidence: number }[] = [];

  constructor() {
    super();
    
    // Initialize the form with validation
    this.form = this.fb.group({
      // IDs
      baaId: ['', Validators.required],
      baaNum: [''],
      mainDocumentName: [''],
      
      // Parties
      party: ['', Validators.required],
      businessAssociateName: [''],
      coveredEntity: [''],
      
      // Legal entities
      legalEntities: [[]],
      
      // Owners
      primaryOwner: [''],
      secondaryOwner: [''],
      tertiaryOwner: [''],
      
      // Signatories
      baSignatory: [''],
      ceSignatory: [''],
      
      // Notifications
      businessAssociateNotification: [''],
      coveredEntityNotification: [''],
      
      // Coordinators
      baCoordinator: [''],
      ceCoordinator: [''],
      
      // Status and types
      status: [''],
      typesOfService: [[]],
      renewalType: [''],
      
      // Dates
      executionDate: [''],
      ceExecutionDate: [''],
      effectiveDate: ['', Validators.required],
      expirationDate: [''],
      nextReviewDate: [''],
      lastReviewDate: [''],
      
      // Terms
      initialTerm: [''],
      
      // Additional fields
      departments: [[]],
      sites: [[]],
      description: [''],
      missingSignature: [false]
    });
  }

  async ngOnInit() {
    // Set loading state immediately when component initializes
    this.loadingBaaData = true;

    try {
      const baaNum = this.route.snapshot.paramMap.get('id');
      const navigation = this.router.getCurrentNavigation();
      const state = navigation?.extras?.state || history.state;

      // Load static field values
      await this.getStaticFieldValues();

      // Load autocomplete data for dropdown filtering.
      //
      // settledCache, NOT firstValueFrom: these caches are filled by
      // `getAllPaginatedDataProgressively`, which re-emits a GROWING array once
      // per page. firstValueFrom took the FIRST emission — roughly page one, or
      // an empty array if no page had landed yet — so the pickers showed an
      // arbitrary ~100-record slice of cbhi's ~4,500 parties and never updated.
      // Users read that as "whole categories of party are missing".
      // settledCache waits for the stream to go quiet and returns the full list.
      //
      // Loaded in parallel: the caches are already fetching concurrently, so
      // awaiting them one at a time only stacks up each one's quiet-period wait.
      [
        this.filteredUsers,
        this.filteredSites,
        this.filteredLegalEntities,
        this.filteredParties,
        this.filteredPeople
      ] = await Promise.all([
        settledCache(this.allUsers$),
        settledCache(this.allSites$),
        settledCache(this.allLegalEntities$),
        settledCache(this.allParties$),
        settledCache(this.allPeople$)
      ]);

      if (baaNum) {
        // Load BAA from service using ID
        this.isEditMode = true;
        await this.loadBaaByNum(baaNum);
      } else if (state?.baa) {
        // BAA passed via router state
        this.isEditMode = true;
        this.currentBaa = state.baa;
        this.originalBaa = { ...state.baa };
        this.populateFormWithBaa(this.currentBaa);
      } else {
        this.isEditMode = false;
        // Brand new BAA gets new baaId and baaNum
        this.form.patchValue({
          baaId: this.generateBaaId(),
          baaNum: Date.now().toString()
        });
      }

      // Workflow Request front-door: accept a file pre-selected by the intake dialog.
      const prefilledFile = state?.prefilledFile as File | undefined;
      if (prefilledFile instanceof File) {
        this.handleFileUpload(prefilledFile);
        this.fromWorkflowRequest = true;

        const existingAnalysis = state?.intakeAnalysisResult;
        if (existingAnalysis?.status === 'complete' && existingAnalysis?.extraction) {
          console.log('%c[NewBaa] Reusing AI analysis from workflow-request dialog',
            'color:#1976d2;font-weight:bold;', existingAnalysis);
          setTimeout(() => this.applyExistingAnalysis(existingAnalysis), 0);
        }
      }

      // Subscribe to form changes for tracking edits
      this.formSubscription = this.form.valueChanges.subscribe(changes => {
        this.currentBaa = { ...this.currentBaa, ...changes };
      });

    } finally {
      // Always turn off loading state when done (success or error)
      this.loadingBaaData = false;

      // Force change detection to update the UI
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  // ----- AI Analysis auto-fill (from workflow-request dialog) -----

  private applyExistingAnalysis(response: IntakeAnalysisResponse): void {
    this.intakeAnalysisResults = response;
    this.aiSummary = response.extraction?.summary || 'BAA analyzed successfully.';
    this.showAISummary = true;
    this.autoFillBaaFromExtraction(response);
    this.cdr.detectChanges();
  }

  private autoFillBaaFromExtraction(response: IntakeAnalysisResponse): void {
    const ext = response.extraction;
    if (!ext) return;

    const baaFields = ext.baaFields;
    const updates: any = {};
    this.autoPopulatedFieldsDisplay = [];

    // Common fields
    if (ext.effectiveDate?.normalized) {
      updates.effectiveDate = new Date(ext.effectiveDate.normalized + 'T00:00:00');
      this.autoPopulatedFieldsDisplay.push({
        label: 'Effective Date',
        value: ext.effectiveDate.extracted || ext.effectiveDate.normalized,
        confidence: ext.effectiveDate.confidence || 0
      });
    }
    if (ext.expirationDate?.normalized) {
      updates.expirationDate = new Date(ext.expirationDate.normalized + 'T00:00:00');
      this.autoPopulatedFieldsDisplay.push({
        label: 'Expiration Date',
        value: ext.expirationDate.extracted || ext.expirationDate.normalized,
        confidence: ext.expirationDate.confidence || 0
      });
    }

    // BAA-specific fields
    if (baaFields) {
      if (baaFields.coveredEntity) {
        updates.coveredEntity = baaFields.coveredEntity;
        this.autoPopulatedFieldsDisplay.push({ label: 'Covered Entity', value: baaFields.coveredEntity, confidence: 0.9 });
      }
      if (baaFields.businessAssociate) {
        updates.businessAssociateName = baaFields.businessAssociate;
        this.autoPopulatedFieldsDisplay.push({ label: 'Business Associate', value: baaFields.businessAssociate, confidence: 0.9 });
      }
      if (baaFields.typesOfService?.length) {
        updates.typesOfService = baaFields.typesOfService;
        this.autoPopulatedFieldsDisplay.push({ label: 'Types of Service', value: baaFields.typesOfService.join(', '), confidence: 0.85 });
      }
      if (baaFields.renewalType) {
        updates.renewalType = baaFields.renewalType;
        this.autoPopulatedFieldsDisplay.push({ label: 'Renewal Type', value: baaFields.renewalType, confidence: 0.85 });
      }
      if (baaFields.initialTerm?.normalizedMonths) {
        updates.initialTerm = baaFields.initialTerm.normalizedMonths;
        this.autoPopulatedFieldsDisplay.push({
          label: 'Initial Term',
          value: baaFields.initialTerm.extracted || `${baaFields.initialTerm.normalizedMonths} months`,
          confidence: baaFields.initialTerm.confidence || 0
        });
      }
      if (baaFields.description) {
        updates.description = baaFields.description;
        this.autoPopulatedFieldsDisplay.push({ label: 'Description', value: baaFields.description, confidence: 0.8 });
      }
      if (baaFields.missingSignature !== undefined) {
        updates.missingSignature = baaFields.missingSignature;
      }
    }

    if (Object.keys(updates).length > 0) {
      this.form.patchValue(updates);
      console.log('%c[NewBaa] Auto-filled from AI extraction:', 'color:#4caf50;font-weight:bold;', updates);
    }
  }

  async loadBaaByNum(baaNum: string) {
    this.loadingBaaData = true;
    
    try {
      // settledCache, not firstValueFrom — this lookup decides whether to load the
      // record or bounce the user out with "BAA not found". Reading a partial
      // (or still-empty) cache made editing an existing BAA fail at random,
      // depending purely on which page of results had landed.
      const baas = await settledCache(this.baaService.visibleBaas$);
      const baa = baas.find(b => b.baaNum === baaNum);
      
      if (baa) {
        //console.log('BAA found:', baa);
        this.currentBaa = baa;
        this.originalBaa = { ...baa };
        this.populateFormWithBaa(baa);
      } else {
        console.error('BAA not found with ID:', baaNum);
        alert('BAA not found');
        this.router.navigate(['/securehome/baas']);
      }
    } catch (error) {
      console.error('Error loading BAA:', error);
      alert('Failed to load BAA');
    } finally {
      this.loadingBaaData = false;
    }
  }

  populateFormWithBaa(baa: Baa) {
    //console.log('Populating form with BAA:', baa);

    this.form.patchValue({
      baaId: baa.baaId,
      baaNum: baa.baaNum,
      mainDocumentName: baa.mainDocumentName ?? '',
      party: baa.party,
      businessAssociateName: baa.businessAssociateName,
      coveredEntity: baa.coveredEntity,
      legalEntities: baa.legalEntities,
      primaryOwner: baa.primaryOwner,
      secondaryOwner: baa.secondaryOwner,
      tertiaryOwner: baa.tertiaryOwner,
      baSignatory: baa.baSignatory,
      ceSignatory: baa.ceSignatory,
      businessAssociateNotification: baa.businessAssociateNotification,
      coveredEntityNotification: baa.coveredEntityNotification,
      baCoordinator: baa.baCoordinator,
      ceCoordinator: baa.ceCoordinator,
      status: baa.status,
      typesOfService: baa.typesOfService,
      renewalType: baa.renewalType,
      executionDate: baa.executionDate,
      ceExecutionDate: baa.ceExecutionDate,
      effectiveDate: baa.effectiveDate,
      expirationDate: baa.expirationDate,
      nextReviewDate: baa.nextReviewDate,
      lastReviewDate: baa.lastReviewDate,
      initialTerm: baa.initialTerm,
      departments: Array.isArray(baa.departments) ? baa.departments : [],
      sites: this.extractSitesForUI(baa.sites),
      description: baa.description,
      missingSignature: baa.missingSignature
    });
    
    // Handle existing file
    if (baa.linkToBaa) {
      this.loadDocument(baa.linkToBaa);
    }
  }

  /**
   * Extracts sites for UI display from backend data
   * Converts array of site IDs to array of full site objects
   */
  extractSitesForUI(sites: any): any[] {
    if (!sites || !Array.isArray(sites)) {
      return [];
    }

    if (sites.length === 0) {
      return [];
    }

    // If sites are already hydrated objects with name property
    if (sites[0]?.name) {
      return sites;
    }

    // If sites are already hydrated objects with siteName property
    if (sites[0]?.siteName) {
      return sites.map(s => ({ ...s, name: s.siteName }));
    }

    // If sites are ID strings, map them to full site objects
    if (typeof sites[0] === 'string') {
      const mappedSites = sites
        .map(siteId => {
          const found = this.filteredSites.find(s => s.siteNum === siteId);
          return found;
        })
        .filter(site => site);
      return mappedSites;
    }

    // If sites are objects with siteData nested structure
    if (sites[0]?.siteData) {
      return sites.map(s => s.siteData);
    }

    return sites;
  }

  // File Upload Methods
  // File Upload Methods moved to CreateEntity base class
  // (onDragOver, onDragLeave, onDrop, onFileSelected, handleFileUpload, removeFile)

  // Validation
  isFormValid(): boolean {
    const requiredFields = ['baaId', 'party', 'effectiveDate'];
    return requiredFields.every(field => this.form.get(field)?.value);
  }

  getValidationMessage(): string {
    if (!this.form.get('baaId')?.value) return 'BAA ID is required';
    if (!this.form.get('party')?.value) return 'Party is required';
    if (!this.form.get('effectiveDate')?.value) return 'Effective date is required';
    return '';
  }


  async getStaticFieldValues() {
    this.statusTypes = await firstValueFrom(this.commonSheetsService.getContractStatusTypes());
    this.serviceTypes = await firstValueFrom(this.commonSheetsService.getContractTypes());
    //  Service types for BAA uses contract type according to Ray 11/24/2025
    // this.serviceTypes = await firstValueFrom(this.commonSheetsService.getServiceTypes());
    this.renewalTypeLabels = await firstValueFrom(this.commonSheetsService.getRenewalTypeLabels());
    this.allDepartments = await firstValueFrom(this.commonSheetsService.getDepartments());
  }

  displayUserFn(user: any): string {
    return user && user.friendlyName ? user.friendlyName : '';
  }

  displayPartyFn(party: any): string {
    return party && party.name ? party.name : '';
  }

  displayPersonFn(person: any): string {
    return person && person.fullName ? person.fullName : '';
  }

  displayLegalEntityFn(entity: any): string {
    return entity && entity.legalEntityName ? entity.legalEntityName : '';
  }

  // Override filter methods to work with Material Design autocomplete
  // Material sends Event objects with event.target.value, not { query: string } like PrimeNG
  filterParties(event: any): void {
    const query = event.target?.value || '';
    this.filterAndSetList(
      this.partyService.allParties$,
      'filteredParties',
      'name',
      query
    );
  }

  filterPeople(event: any): void {
    const query = event.target?.value || '';
    this.filterAndSetList(
      this.peopleService.allPeople$,
      'filteredPeople',
      'fullName',
      query
    );
  }

  filterUsers(event: any): void {
    const query = event.target?.value || '';
    this.filterAndSetList(
      this.usersService.allUsers$,
      'filteredUsers',
      'friendlyName',
      query
    );
  }

  filterLegalEntity(event: any): void {
    const query = event.target?.value || '';
    this.filterAndSetList(
      this.legalEntityService.allLegalEntities$,
      'filteredLegalEntities',
      'legalEntityName',
      query
    );
  }

  cancelSubmit() {
    this.router.navigateByUrl('/securehome/baas');
  }

  async uploadBaaFileToS3(file: File, baaId: string): Promise<string> {
    try {
      const fileExtension = file.name.split('.').pop();
      const uniqueFileName = `baas/${baaId}/${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExtension}`;

      const preSignedUrlResponse = await firstValueFrom(
        this.fileUploadService.getPresignedUrl(uniqueFileName)
      );
      const preSignedUrl = preSignedUrlResponse.presignedUrl;
      
      await firstValueFrom(
        this.fileUploadService.uploadFileToS3(preSignedUrl, file)
      );

      return preSignedUrl.split('?')[0];
      
    } catch (error) {
      console.error('S3 upload failed:', error);
      throw error;
    }
  }

  async onSubmit() {
    if (!this.isFormValid()) {
      alert(this.getValidationMessage());
      return;
    }

    this.saving = true;
    const baaToSave: Baa = this.form.value;

    if (!baaToSave.baaId) {
      alert('BAA ID is missing');
      this.saving = false;
      return;
    }

    // Upload new file to S3 if present
    if (this.uploadedFile) {
      try {
        const fileUrl = await this.uploadBaaFileToS3(this.uploadedFile, baaToSave.baaId);
        baaToSave.linkToBaa = fileUrl;
      } catch (error) {
        console.error('Failed to upload file:', error);
        this.saving = false;
        alert('Failed to upload BAA file. Please try again.');
        return;
      }
    } else if (this.currentDocument?.directLink) {
      baaToSave.linkToBaa = this.currentDocument.directLink;
    }

    const action = this.isEditMode ? DatabaseAction.Edit : DatabaseAction.Create;

    this.baaService.writeBaa(baaToSave).subscribe({
      next: async (response) => {
        //console.log('BAA saved successfully', response);

        // Update relationships if needed
        if (this.isEditMode && this.originalBaa) {
          try {
            await this.updateBaaRelationships(baaToSave, this.originalBaa);
          } catch (relationshipError) {
            console.error('Failed to update relationships:', relationshipError);
            // Don't fail the entire save if relationships fail
          }
        }

        this.saving = false;
        // Navigate to the BAA record view instead of the list
        this.router.navigate(['/securehome/baa', baaToSave.baaNum]);
      },
      error: (err) => {
        console.error('Failed to save BAA:', err);
        this.saving = false;
        alert('Failed to save BAA. Please try again.');
      }
    });
  }

  /**
   * Updates BAA relationships when entities change
   */
  private async updateBaaRelationships(newBaa: Baa, oldBaa: Baa): Promise<void> {
    const baaRef = {
      type: 'baa' as const,
      id: newBaa.baaId!
    };

    // Track relationship fields that need updating
    const relationshipFields = [
      { field: 'party', oldValue: oldBaa.party, newValue: newBaa.party },
      { field: 'legalEntities', oldValue: oldBaa.legalEntities, newValue: newBaa.legalEntities }
    ];

    // Update each relationship field that changed
    for (const { field, oldValue, newValue } of relationshipFields) {
      // Skip if values are the same
      if (JSON.stringify(oldValue) === JSON.stringify(newValue)) {
        continue;
      }

      // Update the relationship
      await this.relationshipService.update(
        baaRef,
        field,
        oldValue,
        newValue
      );
    }

    //console.log('BAA relationships updated successfully');
  }

  generateBaaId(): string {
    return generateBaaId();
  }

  /**
   * Compare sites by siteNum for mat-select pre-selection
   * Allows mat-select to match site objects by property instead of reference
   */
  compareSites(site1: any, site2: any): boolean {
    return site1 && site2 ? site1.siteNum === site2.siteNum : site1 === site2;
  }
}